/**
 * FN-FE-03 + FN-FIX-B1 — Step 5: Trial-indítás megerősítés + logó feltöltés (opcionális)
 */
import { useState, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import Button from '../../components/ui/Button';
import { post } from '../../lib/api';
import { useToast } from '../../components/ui/Toast';
import { StepDots } from './OnboardingCompanyStep';

export default function OnboardingConfirmStep() {
  const navigate = useNavigate();
  const { token } = useAuth();
  const { toast } = useToast();
  const [loading, setLoading] = useState(false);
  const [logoUploaded, setLogoUploaded] = useState(false);
  const [uploading, setUploading] = useState(false);
  const fileRef = useRef<HTMLInputElement>(null);

  async function handleStart() {
    setLoading(true);
    try {
      await post('/setup/start-trial', {});
      navigate('/');
    } catch (err: any) { toast(err.message || 'Hiba történt.', 'error'); } finally { setLoading(false); }
  }

  async function handleLogoUpload(file: File) {
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append('logo', file);
      const tk = localStorage.getItem('accessToken');
      const res = await fetch('/api/company-profile/logo', { method: 'POST', headers: tk ? { Authorization: `Bearer ${tk}` } : {}, body: fd });
      const data = await res.json();
      if (res.ok) { setLogoUploaded(true); toast('Logó feltöltve!', 'success'); }
      else { toast(data.error || 'Feltöltés sikertelen.', 'error'); }
    } catch { toast('Feltöltés sikertelen.', 'error'); } finally { setUploading(false); }
  }

  return (
    <div className="onb-bg">
      <div className="onb-wrap">
        <StepDots current={5} total={5} />

        <h1 className="onb-title">Készen állsz!</h1>
        <p className="onb-sub">Az alábbiakkal indul a 30 napos ingyenes próbád.</p>

        <div className="onb-card">
          <div className="space-y-4">
            {/* Logó feltöltés — opcionális */}
            <div style={{ border: '1px dashed var(--line)', borderRadius: 'var(--r)', padding: '16px', textAlign: 'center' }}>
              <div style={{ fontWeight: 700, fontSize: '14px', color: 'var(--navy)', marginBottom: '8px' }}>Céglogó feltöltése (opcionális)</div>
              <div style={{ fontSize: '12px', color: 'var(--slate)', marginBottom: '12px' }}>
                Megjelenik a számlákon, szállítóleveleken és a rendszer bal felső sarkában.
              </div>
              <input ref={fileRef} type="file" accept="image/png,image/jpeg,image/webp" style={{ display: 'none' }} onChange={e => { if (e.target.files?.[0]) handleLogoUpload(e.target.files[0]); }} />
              {logoUploaded ? (
                <div style={{ color: 'var(--green)', fontWeight: 700, fontSize: '13px' }}>✓ Logó feltöltve</div>
              ) : (
                <Button size="sm" variant="ghost" loading={uploading} onClick={() => fileRef.current?.click()}>📷 Logó kiválasztása</Button>
              )}
              <div style={{ fontSize: '11px', color: 'var(--slate-2)', marginTop: '6px' }}>PNG, JPG vagy WEBP · max 10 MB · Bármikor módosíthatod a Beállításokban</div>
            </div>

            {/* Próba összesítő */}
            <div style={{ fontSize: '14px' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', padding: '8px 0', borderBottom: '1px solid var(--line-2)' }}>
                <span style={{ color: 'var(--slate)' }}>Próba kezdete</span>
                <span style={{ fontWeight: 700, color: 'var(--navy)' }}>Ma</span>
              </div>
              <div style={{ display: 'flex', justifyContent: 'space-between', padding: '8px 0', borderBottom: '1px solid var(--line-2)' }}>
                <span style={{ color: 'var(--slate)' }}>Próba lejárata</span>
                <span style={{ fontWeight: 700, color: 'var(--navy)' }}>30 nap múlva</span>
              </div>
              <div style={{ display: 'flex', justifyContent: 'space-between', padding: '8px 0' }}>
                <span style={{ color: 'var(--slate)' }}>Bankkártya szükséges?</span>
                <span style={{ fontWeight: 700, color: 'var(--green)' }}>Nem</span>
              </div>
            </div>

            <div style={{ background: 'var(--orange-soft)', padding: '14px 16px', borderRadius: '10px' }}>
              <div style={{ fontWeight: 700, fontSize: '14px', color: 'var(--orange-deep)', marginBottom: '4px' }}>A 15. napon 10% kedvezmény</div>
              <div style={{ fontSize: '13px', color: 'var(--slate)' }}>Ha a próba 15. napján előfizetsz, 10% kedvezményt kapsz az első fizetésednél.</div>
            </div>

            <div style={{ fontSize: '13px', color: 'var(--slate)', lineHeight: 1.6 }}>
              A próba végén figyelmeztetünk, és csak akkor fizetsz, ha tovább szeretnél maradni. Az előfizetés nem indul el automatikusan.
            </div>
          </div>

          <div className="onb-footer" style={{ justifyContent: 'space-between' }}>
            <Button variant="ghost" onClick={() => navigate('/onboarding/plan')}>← Vissza</Button>
            <Button loading={loading} onClick={handleStart}>Indul a próba →</Button>
          </div>
        </div>
      </div>
    </div>
  );
}
